import api from '@/lib/api';
import { isNative } from '@/services/platform';

let VAPID_PUBLIC_KEY = null;

class NotificationService {
  constructor() {
    this.registration = null;
    this.subscription = null;
    this.isSupported = 'Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window;
  }

  // Fetch VAPID public key from backend
  async getVapidKey() {
    if (VAPID_PUBLIC_KEY) return VAPID_PUBLIC_KEY;
    
    try {
      const response = await api.get('/push/vapid-public-key');
      VAPID_PUBLIC_KEY = response.data.public_key;
      return VAPID_PUBLIC_KEY;
    } catch (error) {
      console.error('Failed to get VAPID key:', error);
      return null;
    }
  }

  // Check if notifications are supported
  checkSupport() {
    return isNative() || this.isSupported;
  }

  // Get current permission status
  getPermissionStatus() {
    if (!this.isSupported) return 'unsupported';
    return Notification.permission; // 'granted', 'denied', 'default'
  }

  async hasPermission() {
    if (isNative()) {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const perm = await PushNotifications.checkPermissions();
        return perm.receive === 'granted';
      } catch {
        return false;
      }
    }
    return this.getPermissionStatus() === 'granted';
  }

  // Request notification permission
  async requestPermission() {
    if (isNative()) {
      const { PushNotifications } = await import('@capacitor/push-notifications');
      const perm = await PushNotifications.requestPermissions();
      return perm.receive;
    }
    if (!this.isSupported) {
      throw new Error('Notifications not supported');
    }

    const permission = await Notification.requestPermission();
    return permission;
  }

  // Register service worker
  async registerServiceWorker() {
    if (isNative()) return null;
    if (!('serviceWorker' in navigator)) {
      throw new Error('Service Worker not supported');
    }

    try {
      this.registration = await navigator.serviceWorker.register('/sw.js');
      console.log('Service Worker registered:', this.registration);
      return this.registration;
    } catch (error) {
      console.error('Service Worker registration failed:', error);
      throw error;
    }
  }

  // Subscribe to push notifications
  async subscribeToPush() {
    if (isNative()) {
      return this.initialize();
    }
    const vapidKey = await this.getVapidKey();
    if (!vapidKey) {
      console.warn('Push notifications not configured on server');
      return null;
    }

    if (!this.registration) {
      await this.registerServiceWorker();
    }

    try {
      // Check if already subscribed
      let subscription = await this.registration.pushManager.getSubscription();
      
      if (!subscription) {
        // Create new subscription
        subscription = await this.registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: this.urlBase64ToUint8Array(vapidKey)
        });
      }

      this.subscription = subscription;
      
      // Send subscription to backend
      await this.sendSubscriptionToServer(subscription);
      
      console.log('Push subscription successful');
      return subscription;
    } catch (error) {
      console.error('Push subscription failed:', error);
      throw error;
    }
  }

  // Send subscription to backend
  async sendSubscriptionToServer(subscription) {
    try {
      await api.post('/push/subscribe', subscription.toJSON());
      console.log('Subscription sent to server');
    } catch (error) {
      console.error('Failed to send subscription to server:', error);
    }
  }

  // Unsubscribe from push notifications
  async unsubscribe() {
    if (this.subscription) {
      const endpoint = this.subscription.endpoint;
      await this.subscription.unsubscribe();
      this.subscription = null;
      
      // Notify backend
      try {
        await api.post('/push/unsubscribe', { endpoint });
      } catch (error) {
        console.error('Failed to unsubscribe on server:', error);
      }
    }
  }

  // Show local notification (for testing or immediate alerts)
  async showLocalNotification(title, options = {}) {
    if (!this.isSupported) {
      console.warn('Notifications not supported');
      return;
    }

    if (Notification.permission !== 'granted') {
      console.warn('Notification permission not granted');
      return;
    }

    const defaultOptions = {
      icon: '/logo192.png',
      badge: '/logo192.png',
      vibrate: [200, 100, 200],
      tag: 'workplus-local',
      requireInteraction: false,
      ...options
    };

    if (this.registration) {
      // Use service worker notification
      await this.registration.showNotification(title, defaultOptions);
    } else {
      // Fallback to regular notification
      new Notification(title, defaultOptions);
    }
  }

  // Show violation notification
  async showViolationNotification(violation) {
    await this.showLocalNotification('Work+ Violation Alert', {
      body: violation.description || violation.violation_reason || 'Rule violation detected',
      tag: `violation-${violation.id || Date.now()}`,
      data: {
        type: 'violation',
        violation_id: violation.id,
        url: '/violations'
      }
    });
  }

  // Show status change notification
  async showStatusNotification(status, objectName) {
    const statusMessages = {
      working: `You're now working at ${objectName}`,
      pause: 'Pause started',
      lunch: 'Lunch break started',
      outside: `You've left ${objectName || 'the work site'}`,
      no_gps: 'GPS signal lost - time not being tracked'
    };

    await this.showLocalNotification('Work+ Status', {
      body: statusMessages[status] || `Status: ${status}`,
      tag: 'status-change',
      data: {
        type: 'status',
        status: status,
        url: '/dashboard'
      }
    });
  }

  // Test push notification
  async testPush() {
    try {
      const response = await api.post('/push/test');
      return response.data;
    } catch (error) {
      console.error('Test push failed:', error);
      throw error;
    }
  }

  // Helper: Convert VAPID key
  urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  }

  // Listen for service worker messages
  listenForMessages(callback) {
    if (isNative()) return;
    if (!('serviceWorker' in navigator)) return;

    navigator.serviceWorker.addEventListener('message', (event) => {
      callback(event.data);
    });
  }

  async prepare() {
    if (isNative()) return true;
    if (!this.checkSupport()) return false;
    try {
      await this.registerServiceWorker();
      return true;
    } catch {
      return false;
    }
  }

  // Initialize notifications (request permission + subscribe)
  async initialize() {
    // In native app, skip Service Worker — use Capacitor Push Notifications
    if (isNative()) {
      try {
        const { PushNotifications } = await import('@capacitor/push-notifications');
        const perm = await PushNotifications.requestPermissions();
        if (perm.receive === 'granted') {
          try {
            await PushNotifications.register();
          } catch {}
          PushNotifications.removeAllListeners().catch(() => {});
          PushNotifications.addListener('registration', (token) => {
            api.post('/push/subscribe-fcm', { token: token.value }).catch(() => {});
            console.log('[Push] FCM token registered:', token.value);
          });
          PushNotifications.addListener('pushNotificationReceived', (notification) => {
            console.log('[Push] Received:', notification);
          });
          PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
            console.log('[Push] Action:', action);
            // Navigate based on notification data
            const data = action.notification.data;
            if (data?.url) {
              window.location.hash = data.url;
            }
          });
          return true;
        }
        return false;
      } catch (err) {
        console.warn('[Push] Native init failed:', err);
        return false;
      }
    }

    // Web: use Service Worker + VAPID
    if (!this.checkSupport()) {
      console.warn('Push notifications not supported');
      return false;
    }

    const permission = await this.requestPermission();
    if (permission !== 'granted') {
      console.warn('Notification permission denied');
      return false;
    }

    await this.registerServiceWorker();
    await this.subscribeToPush();
    return true;
  }
}

export const notificationService = new NotificationService();
export default notificationService;
